import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { PaaDiffBox, PaaSheetChange } from "../api";

// The two drawings, side by side: the machinery both review passes share.
//
// It lives here rather than inside one of them because there are now two
// surfaces that put an approved page next to an updated one - settling what
// supersedes what, and checking a description against the drawings. They differ
// in the QUESTION they ask, not in how the drawings behave: the panning is
// synchronised either way, the blink works the same, and the layout she arranges
// has to survive moving between sheets on both.
//
// Copying it would be this codebase's own documented trap. Two of anything
// eventually disagree, and what would drift here is the pan/zoom feel - which
// she would notice on one page and not the other without being able to say why.

type PaneState = "loading" | "ready" | "missing" | "expired" | "error";

/** Which pane is folded away. */
type Collapsed = "old" | "new" | null;

// How the drawings are arranged, remembered.
//
// Radiology settled this one: a hanging protocol applies a known arrangement
// automatically so the reader never arranges windows, and it is per-reader. A
// guided flow that makes her re-collapse a pane and re-find her zoom on all
// thirty-five sheets is a worse deal than the modal it replaced.
interface Layout {
  fit: boolean;
  zoom: number;
  showMarkers: boolean;
  collapsed: Collapsed;
}

const LAYOUT_KEY = "paa.compare.layout";
const DEFAULT_LAYOUT: Layout = { fit: true, zoom: 1, showMarkers: true, collapsed: null };

function loadLayout(): Layout {
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const saved = JSON.parse(raw) as Partial<Layout>;
    return {
      fit: saved.fit ?? DEFAULT_LAYOUT.fit,
      // Clamped rather than trusted: a stored 40x zoom from a bad write would
      // render one pixel of a plan sheet and look like a broken viewer.
      zoom: Math.min(6, Math.max(0.25, saved.zoom ?? DEFAULT_LAYOUT.zoom)),
      showMarkers: saved.showMarkers ?? DEFAULT_LAYOUT.showMarkers,
      collapsed:
        saved.collapsed === "old" || saved.collapsed === "new" ? saved.collapsed : null,
    };
  } catch {
    // Private windows, cleared site data, storage disabled. A remembered layout
    // is a convenience; failing to read one is not an error.
    return DEFAULT_LAYOUT;
  }
}

/**
 * The remembered arrangement.
 *
 * Radiology settled this one: a hanging protocol applies a known arrangement
 * automatically so the reader never arranges windows, and it is per-reader. A
 * guided flow that makes her re-collapse a pane and re-find her zoom on every
 * sheet is a worse deal than the modal it replaced.
 */
export function useSheetLayout() {
  const [layout, setLayout] = useState<Layout>(loadLayout);
  // Accepts a function of the CURRENT layout as well as a plain patch. That is
  // not a nicety: a zoom button written as `zoom: zoom + 0.25` reads `zoom` from
  // the render that created the handler, so five quick clicks all compute the
  // same step and advance one. Felt exactly like a button that drops presses.
  const patchLayout = useCallback(
    (patch: Partial<Layout> | ((prev: Layout) => Partial<Layout>)) => {
    setLayout((prev) => {
      const next = { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) };
      try {
        window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(next));
      } catch {
        // Not worth telling her about; the layout simply won't persist.
      }
      return next;
    });
    },
    [],
  );
  return { layout, patchLayout };
}

/**
 * Two scroll panes that follow each other, so the same region of both drawings
 * stays under the eye. `syncing` is the re-entry guard: without it the two
 * handlers drive each other in a loop.
 */
export function useSyncedPanes() {
  const oldPaneRef = useRef<HTMLDivElement>(null);
  const newPaneRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  const syncScroll = useCallback((from: "old" | "new") => {
    if (syncing.current) return;
    const src = from === "old" ? oldPaneRef.current : newPaneRef.current;
    const dst = from === "old" ? newPaneRef.current : oldPaneRef.current;
    if (!src || !dst) return;
    syncing.current = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  }, []);

  return { oldPaneRef, newPaneRef, syncing, syncScroll };
}

/** False only while a bare letter binding is safe - i.e. she is not typing.
 *  The moment either surface holds a text field, an unguarded "n" advances the
 *  sheet and eats the keystroke. */
export function isTypingInto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
}

export function ZoomButton({
  active,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        "h-7 min-w-[2rem] rounded-md border px-2 text-[0.8rem] font-medium transition-colors",
        active
          ? "border-primary bg-primary-soft text-primary-ink"
          : "border-rule-strong bg-surface text-ink-soft hover:border-primary hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

export const SheetPane = forwardRef<
  HTMLDivElement,
  {
    label: string;
    url: string;
    fit: boolean;
    zoom: number;
    accent?: boolean;
    /** The other side's drawing, held loaded so a blink is instant. */
    blinkUrl?: string;
    blink?: boolean;
    changes: PaaSheetChange[];
    side: "old" | "new";
    current: number;
    onPickChange: (index: number) => void;
    onScroll: () => void;
  }
>(({ label, url, fit, zoom, accent, blinkUrl, blink, changes, side, current, onPickChange, onScroll }, ref) => {
  const [state, setState] = useState<PaneState>("loading");
  // Bumped to re-request the same url after a load that failed for a reason the
  // server does not agree with. See onError.
  const [attempt, setAttempt] = useState(0);

  // A fresh url (different sheet) starts over — otherwise the previous drawing
  // lingers while the new one loads.
  useEffect(() => {
    setState("loading");
    setAttempt(0);
  }, [url]);

  return (
    <section className="flex min-h-0 min-w-0 flex-col bg-canvas">
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 border-b border-rule px-4 py-1.5 text-[0.78rem] font-medium",
          accent ? "bg-sage-soft text-sage-ink" : "bg-surface-2 text-ink-soft",
        )}
      >
        {label}
      </div>
      <div
        ref={ref}
        onScroll={onScroll}
        className={cn(
          "min-h-0 flex-1 bg-canvas-2 p-3",
          // Fit has nothing to scroll, and centring it stops a landscape sheet
          // sitting against the top of a tall pane with half the height empty.
          fit ? "flex items-center justify-center overflow-hidden" : "overflow-auto",
        )}
      >
        {state === "loading" && (
          <div className="flex h-full items-center justify-center gap-2.5 text-[0.85rem] text-ink-muted">
            <span
              aria-hidden
              className="h-4 w-4 animate-spin rounded-full border-2 border-rule border-t-primary"
            />
            Rendering the drawing…
          </div>
        )}
        {state === "missing" && (
          <div className="flex h-full items-center justify-center px-6 text-center text-[0.85rem] text-ink-muted">
            This sheet isn&apos;t in the {label.toLowerCase()} set.
          </div>
        )}
        {state === "expired" && (
          <div className="mx-auto flex h-full max-w-sm flex-col items-center justify-center gap-1.5 px-6 text-center">
            <div className="text-[0.9rem] font-medium text-ink">
              The comparison is no longer loaded
            </div>
            <div className="text-[0.85rem] leading-relaxed text-ink-muted">
              Both plan sets are held in memory on the server, and that is
              cleared when the app restarts or after 30 idle minutes. Your notes
              are safe — close this and run the comparison again to view the
              drawings.
            </div>
          </div>
        )}
        {state === "error" && (
          <div className="flex h-full items-center justify-center px-6 text-center text-[0.85rem] text-ink-muted">
            Couldn&apos;t render this drawing.
          </div>
        )}

        {/* The overlay must match the IMAGE box exactly, so it lives in a
            wrapper sized by the image itself rather than the scroll area. */}
        <div
          className={cn(
            "relative",
            state === "ready" ? "inline-block" : "hidden",
            // Shrink-wraps the image either way, so the marker overlay keeps
            // matching the drawing's box exactly.
            fit ? "max-h-full max-w-full" : "block",
          )}
          style={fit ? undefined : { width: `${zoom * 100}%` }}
        >
          <img
            src={attempt === 0 ? url : `${url}&retry=${attempt}`}
            alt={`${label} drawing`}
            onLoad={() => setState("ready")}
            onError={async () => {
              // The <img> only reports "failed", so ask the server what kind of
              // failure it was. 410 = the session is gone (restart / idle
              // timeout), 404 = the sheet genuinely isn't in that set, which is
              // an expected answer for a renumbered sheet.
              try {
                const res = await fetch(url, { method: "HEAD", credentials: "same-origin" });

                // The server says the drawing is there. Observed on the first
                // sheet of a fresh session: the <img> races the session being
                // parked, fails once, and then sat on "Couldn't render this
                // drawing" forever with a perfectly good page one request away
                // — a screen that looks broken when nothing is. Retry once.
                if (res.ok && attempt === 0) {
                  setAttempt(1);
                  setState("loading");
                  return;
                }

                setState(
                  res.status === 404 ? "missing" : res.status === 410 ? "expired" : "error",
                );
              } catch {
                setState("expired");
              }
            }}
            className={cn(
              "block",
              // Bound by whichever edge runs out first — width on a portrait
              // sheet, height on a landscape one, which is most of them.
              fit ? "max-h-full max-w-full object-contain" : "h-auto w-full",
            )}
          />
          {/* The other drawing, same box, opacity-toggled. Rendered whenever a
              blinkUrl is given so it is decoded before the first press: fetching
              on demand would flash white at the exact moment she is looking for
              a difference. `hidden` would unload it, so opacity it is. */}
          {blinkUrl && state === "ready" && (
            <img
              src={blinkUrl}
              alt=""
              aria-hidden
              className={cn(
                "absolute inset-0 block h-full w-full",
                blink ? "opacity-100" : "opacity-0",
              )}
            />
          )}
          {state === "ready" &&
            changes.map((change) => {
              const box = side === "old" ? change.old : change.new;
              if (!box) return null;
              return (
                <Marker
                  key={change.index}
                  box={box}
                  kind={change.kind}
                  isCurrent={change.index === current}
                  index={change.index}
                  onClick={() => onPickChange(change.index)}
                />
              );
            })}
        </div>
      </div>
    </section>
  );
});
SheetPane.displayName = "SheetPane";

// One change marker. Outline only — a fill would hide the line work the
// operator is trying to read. Solid for an exact text change, dashed for an
// approximate graphics region: two styles in ONE colour, so the vocabulary stays
// small enough to learn at a glance.
function Marker({
  box,
  kind,
  isCurrent,
  index,
  onClick,
}: {
  box: PaaDiffBox;
  kind: PaaSheetChange["kind"];
  isCurrent: boolean;
  index: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Change ${index + 1}`}
      className={cn(
        "absolute rounded-[3px] transition-opacity",
        kind === "Graphics" ? "border-2 border-dashed" : "border-2 border-solid",
        isCurrent
          ? "border-primary opacity-100 shadow-[0_0_0_3px_rgba(255,255,255,0.55)]"
          : "border-lilac-ink/45 opacity-60 hover:opacity-100",
      )}
      style={{
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        width: `${box.width * 100}%`,
        height: `${box.height * 100}%`,
        // Minimum hit/visual size so a single thin line of text is still
        // clickable and visible when the whole sheet is zoomed to fit.
        minWidth: 10,
        minHeight: 10,
      }}
    />
  );
}
